import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { DateService, StringService, _ } from "src/services";
import { ProgramCardProps } from "./program.card.props";
import LazyLoad from "vanilla-lazyload";
import { ProgramStatus, ProgramType } from "src/enums";
import { AntentIndicator } from "src/components/svg";
import phSingleItem from "src/assets/static/img/main/icons/ph_single_item.png";
import phTeamLogo from "src/assets/static/img/main/icons/ph_team_logo.png";
import { CardLink } from "./card.link";

export const ProgramCard: React.FC<ProgramCardProps> = ({
  id,
  isLive,
  layout,
  startAt,
  status,
  thumbnail,
  title,
  type,
  upperTitle,
  layoutData,
  page,
  origin,
  liveBadgeText,
}) => {
  //#region dependencies

  const { id: paramId } = useParams<any>();

  //#end region

  //#region methods

  const getLink = () => {
    return `/${page}/${id}/${StringService.sanitize(title)}`;
  };

  //#end region

  //#region properties

  const programIsLive =
    isLive || (type === ProgramType.Live && status === ProgramStatus.Started);

  //#end region

  //#region  effects

  useEffect(() => {
    const lazyload = new LazyLoad({
      elements_selector: ".lazy.pg" + id,
      callback_error: (element: HTMLElement) => {
        const placeholder = element.getAttribute("data-placeholder");
        if (placeholder) element.setAttribute("src", placeholder);
      },
    });

    lazyload.update();
  }, [id]);

  //#end region

  //#region helpers

  const renderThumbnail = () => {
    if (layout === 2 && layoutData) return null;
    return (
      <div className="layout-one-thumb text-left d-inline-block">
        <img
          data-placeholder={phSingleItem}
          src={phSingleItem}
          data-src={thumbnail}
          alt={title}
          className={`lazy pg${id}`}
        />
      </div>
    );
  };

  const renderLogos = () => {
    if (layout === 1 || !layoutData) return null;
    return (
      <div className="col-md-4 col-7 d-flex justify-content-left align-items-center p-0 px-md-2">
        <div className="d-flex bg-logo justify-content-center align-items-center ml-1 ml-md-2">
          <img
            alt={layoutData.guestName}
            data-src={layoutData.guestLogo}
            data-placeholder={phTeamLogo}
            src={phTeamLogo}
            className={`logo lazy pg${id}`}
          />
        </div>
        <div className="icon-vs mx-1 mx-md-2">-</div>
        <div className="d-flex bg-logo justify-content-center align-items-center">
          <img
            alt={layoutData.hostName}
            data-src={layoutData.hostLogo}
            data-placeholder={phTeamLogo}
            src={phTeamLogo}
            className={`logo lazy pg${id}`}
          />
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    return (
      <div
        className={`${
          layout === 1 || !layoutData ? "col-md-9 col-9" : "col-md-8 col-5"
        } d-flex match-content flex-column match-content`}
      >
        <div className="pr-2 pr-md-3 text-rtl">
          <h1 className="match-title text-align-right text-right">{title}</h1>
          <span className="btn-sport-name text-right float-right">
            {upperTitle}
          </span>
        </div>
      </div>
    );
  };

  //#end region

  return (
    <div className="my-3 d-flex">
      <CardLink
        isExternal={!!origin}
        to={origin || getLink()}
        activeClassName="router-link-active"
        isActive={() => paramId === "" + id}
        className="col d-flex no-text-decoration position-relative px-0 pr-1 pr-md-2"
      >
        <div className="card m-0 w-100">
          <span className="indicator-current">
            <AntentIndicator />
          </span>
          <div className={`layout-${layout}-sides program-card text-center`}>
            {renderThumbnail()}
            <div className="card-body py-3">
              <div className="row no-gutters">
                {layout === 1 && <div className="col-md-3 col-3"></div>}
                {renderLogos()}
                {renderTitle()}
              </div>
            </div>
          </div>
        </div>
      </CardLink>
      <div
        className={`${
          programIsLive ? "program-live " : ""
        } program-live-card card px-0 m-0 d-flex justify-content-center align-items-center`}
      >
        <div className="program-card indicator-list">
          <div className="card-body py-2">
            <label className="m-0">
              {DateService.persianDate(startAt, "HH:mm")}
              {programIsLive && (
                <span className="d-block live-badge">
                  {liveBadgeText || _("LIVE")}
                </span>
              )}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
